import BallBankRun, { Curve, PointData } from "./ballBankData";

const DividerRowType = { Start: "START", End: "END", Invalid: "INVALID" };
const BallBankFields = {
  divider: "--", // differentiate from lines with negative numbers by using doubles dash
  road: "ROAD:",
  vicinity: "VICINITY:",
  comment: "COMMENT:",
  date: "DATE:",
  startTime: "START TIME:",
  startDistance: "START DISTANCE:",
  startSpeed: "START SPEED:",
  startLatitude: "START LATITUDE:",
  startLongitude: "START LONGITUDE:",
  degrees: "DEGREES",
  kilometers: "KILOMETERS", // note: US spelling
  kmh: "KMH",
  lat: "LAT",
  long: "LONG",
  endDistance: "END DISTANCE:",
  endSpeed: "END SPEED:",
  endLatitude: "END LATITUDE:",
  endLongitude: "END LONGITUDE:",
  endTime: "END TIME:",
};
const NewCurveDifferential = 0.05;

/**
 * parses the raw BallBank file data into our Run/Curve/Point data
 * @param {string} fileData string representation of the ballbank file data
 */
export function parseRawData(fileData) {
  try {
    const runs = [];
    const rows = fileData.split("\n");
    const runExtents = getRunExtents(rows);

    // in the case that there are multiple start and end sections
    // in a ballbank file a new run is created for each section
    let currentRun = null;
    runExtents.forEach((extents, i, arr) => {
      const previousEndExtent = i > 0 ? arr[i - 1].endRow : null;
      currentRun = createRun(rows, extents.startRow, previousEndExtent);

      let curve = null;
      let lastDist = null;
      let pointDataStartIndexes = [];
      for (var j = extents.startRow; j < extents.endRow; j++) {
        if (rows[j].startsWith("\r")) pointDataStartIndexes.push(j);
      }

      pointDataStartIndexes.forEach((startIndex) => {
        if (startIndex < extents.endRow - 6) {
          const curveType = rows[startIndex + 1].charAt(0);
          const ballBank = parseFloat(
            removeAndTrim(rows[startIndex + 1], [
              BallBankFields.degrees,
              "\r",
            ]).split(" ")[1]
          );
          const refDist = parseFloat(
            removeAndTrim(rows[startIndex + 2], [
              BallBankFields.kilometers,
              "\r",
            ])
          );
          const testSpeed = parseInt(
            removeAndTrim(rows[startIndex + 3], [BallBankFields.kmh, "\r"])
          );
          const latitude = parseFloat(
            removeAndTrim(rows[startIndex + 4], [BallBankFields.lat, "\r"])
          );
          const longitude = parseFloat(
            removeAndTrim(rows[startIndex + 5], [BallBankFields.long, "\r"])
          );
          const point = new PointData(
            ballBank,
            refDist,
            testSpeed,
            latitude,
            longitude
          );

          if (
            !curve ||
            curveType !== curve.curveType ||
            point.refDist > lastDist + NewCurveDifferential
          ) {
            if (curve) currentRun.curves.push(curve);
            curve = new Curve(curveType);
          }
          curve.points.push(point);
          lastDist = point.refDist;
        }
      });
      currentRun.curves.push(curve);
      runs.push(currentRun);
    });

    return runs;
  } catch (err) {
    console.log(err);
    return null;
  }
}

// create a new run from header data rows bounded by the previous end row
// and current start row. If previous end row is null, use the start of file (row = 0)
function createRun(fileRows, startRow, previousEndRow) {
  const headerStartRow = previousEndRow ? previousEndRow : 0;
  const headerEndRow = startRow;
  const headerRows = fileRows.slice(headerStartRow, headerEndRow);
  const roadName = removeAndTrim(
    headerRows.find((r) => r.indexOf(BallBankFields.road) === 0),
    [BallBankFields.road, "\r"]
  );
  const vicinity = removeAndTrim(
    headerRows.find((r) => r.indexOf(BallBankFields.vicinity) === 0),
    [BallBankFields.vicinity, "\r"]
  );
  const comment = removeAndTrim(
    headerRows.find((r) => r.indexOf(BallBankFields.comment) === 0),
    [BallBankFields.comment, "\r"]
  );
  const date = convertBallBankDate(
    removeAndTrim(
      headerRows.find((r) => r.indexOf(BallBankFields.date) === 0),
      [BallBankFields.date, "\r"]
    )
  );

  return new BallBankRun(roadName, vicinity, comment, date);
}

// remove instances of string values from a given string
// trim start and end spaces. return empty string if null/undefined
function removeAndTrim(value, removeValues) {
  let outputValue = value ?? "";
  removeValues.forEach((rem) => {
    outputValue = outputValue.replace(rem, "");
  });
  return outputValue.trim();
}

/**
 * calculate the row numbers which contain the divider rows
 * indicating the start and and end of the run's point data
 * invalid start and end combinations are not returned
 */
function getRunExtents(rows) {
  const curveStartEndRows = [];

  const getAllIndexes = (arr, val) => {
    let indexes = [];
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].indexOf(val) >= 0) indexes.push(i);
    }
    return indexes;
  };

  // retreive all divider rows and determine if they are start or end of a curve
  const dividerRows = getAllIndexes(rows, BallBankFields.divider)
    .sort((a, b) => a - b)
    .map((rowNum) => {
      const isPreviousRowStart =
        rowNum > 0 && rows[rowNum - 1].indexOf("START") === 0;
      const isNextRowEnd =
        rowNum < rows.length - 1 && rows[rowNum + 1].indexOf("END") === 0;
      const dividerType =
        (isPreviousRowStart && isNextRowEnd) ||
        (!isPreviousRowStart && !isNextRowEnd)
          ? DividerRowType.Invalid
          : isPreviousRowStart
          ? DividerRowType.Start
          : DividerRowType.End;

      return { dividerRowNumber: rowNum, dividerType: dividerType };
    });

  // check that the row after a start row is a valid end row
  // create objects for each valid start row and end row combo
  dividerRows.forEach((divider, i, arr) => {
    if (
      divider.dividerType === DividerRowType.Start &&
      i < arr.length - 1 &&
      arr[i + 1].dividerType === DividerRowType.End
    ) {
      curveStartEndRows.push({
        startRow: divider.dividerRowNumber,
        endRow: arr[i + 1].dividerRowNumber,
      });
    }
  });

  return curveStartEndRows;
}

// convert BallBank date format (DD/MM/YYYY) to a format
// able to be converted to a date object later on (YYYY-MM-DD)
function convertBallBankDate(ballBankDate) {
  const day = ballBankDate.slice(0, 2);
  const month = ballBankDate.slice(3, 5);
  const year = ballBankDate.slice(6, 10);
  return `${year}-${month}-${day}`;
}

export const exportedForTesting = {
  createRun,
  removeAndTrim,
  getRunExtents,
  convertBallBankDate,
};
